import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, ArrowUpRight, FileText, Users, Inbox, Mail, Mailbox, HeartHandshake, PackageCheck, Flower, Newspaper, Menu, UtensilsCrossed, Building2, Star, Calendar, ShieldCheck, Database, TrendingDown, RadioTower, Globe, Network, Play, Landmark } from "lucide-react";
import { ArticleEditor } from "@/components/admin/ArticleEditor";
import AdminUsers from "@/components/admin/AdminUsers";
import AdminSubmissions from "@/components/admin/AdminSubmissions";
import AdminMessages from "@/components/admin/AdminMessages";
import AdminCauses from "@/components/admin/AdminCauses";
import AdminPublicSubmissions from "@/components/admin/AdminPublicSubmissions";
import AdminMemorials from "@/components/admin/AdminMemorials";
import AdminNewsSources from "@/components/admin/AdminNewsSources";
import AdminNewsIllustrations from "@/components/admin/AdminNewsIllustrations";
import AdminNavCategories from "@/components/admin/AdminNavCategories";
import AdminRecipes from "@/components/admin/AdminRecipes";
import AdminOrganizations from "@/components/admin/AdminOrganizations";
import AdminWorldChangers from "@/components/admin/AdminWorldChangers";
import AdminInMemoriam from "@/components/admin/AdminInMemoriam";
import AdminEvents from "@/components/admin/AdminEvents";
import AdminServices from "@/components/admin/AdminServices";
import AdminUserVerifications from "@/components/admin/AdminUserVerifications";
import AdminNewsSubmissions from "@/components/admin/AdminNewsSubmissions";
import AdminFamilyComments from "@/components/admin/AdminFamilyComments";
import AdminModeration from "@/components/admin/AdminModeration";
import AdminBackup from "@/components/admin/AdminBackup";
import AdminPremium from "@/components/admin/AdminPremium";
import AdminRetention from "@/components/admin/AdminRetention";
import AdminAccess from "@/components/admin/AdminAccess";
import AdminPulseItems from "@/components/admin/AdminPulseItems";
import AdminLocations from "@/components/admin/AdminLocations";
import AdminMarketingSignups from "@/components/admin/AdminMarketingSignups";
import AdminNetworkSites from "@/components/admin/AdminNetworkSites";
import AdminSatellites from "@/pages/AdminSatellites";
import AdminSeederConsole from "@/pages/admin/AdminSeederConsole";
import AdminEmailTester from "@/pages/admin/AdminEmailTester";
import AdminCemeteryEditor from "@/pages/admin/AdminCemeteryEditor";

const SECTION_LABELS = {
  change_maker: "Change Maker",
  appeal: "Emergency Appeal",
  story: "Success Story",
};

const TABS = [
  { v: "content", label: "Content", icon: FileText },
  { v: "causes", label: "Causes", icon: HeartHandshake },
  { v: "organizations", label: "Organizations", icon: Building2 },
  { v: "world-changers", label: "World Changers", icon: Star },
  { v: "in-memoriam", label: "In Memoriam", icon: Flower },
  { v: "events", label: "Events", icon: Calendar },
  { v: "pulse", label: "Pulse Posts", icon: RadioTower },
  { v: "locations", label: "Locations AI", icon: Globe },
  { v: "services", label: "Services", icon: HeartHandshake },
  { v: "memorials", label: "Memorials", icon: Flower },
  { v: "news", label: "News Sources", icon: Newspaper },
  { v: "news-illustrations", label: "News Illustrations", icon: Newspaper },
  { v: "news-submissions", label: "News Submissions", icon: Inbox },
  { v: "moderation", label: "Moderation", icon: Inbox },
  { v: "family-comments", label: "Family Comments", icon: Inbox },
  { v: "recipes", label: "Recipes", icon: UtensilsCrossed },
  { v: "nav", label: "Explore Menu", icon: Menu },
  { v: "public", label: "Public Submissions", icon: PackageCheck },
  { v: "users", label: "Users", icon: Users },
  { v: "verifications", label: "Verifications", icon: ShieldCheck },
  { v: "submissions", label: "Submissions", icon: Inbox },
  { v: "messages", label: "Messages", icon: Mailbox },
  { v: "backup", label: "Data Backup", icon: Database },
  { v: "premium", label: "Premium Plans", icon: Star },
  { v: "retention", label: "Retention", icon: TrendingDown },
  { v: "access", label: "Access", icon: ShieldCheck },
  { v: "marketing", label: "Marketing", icon: Mailbox },
  { v: "network", label: "Network", icon: Network },
  { v: "satellites", label: "Satellites", icon: Globe },
  { v: "seeder", label: "Seeder", icon: Play },
  { v: "email-tester", label: "Email Tester", icon: Mail },
  { v: "cemeteries", label: "Cemeteries", icon: Landmark },
];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("content");

  if (loading) return <div className="max-w-[1400px] mx-auto px-6 py-24 text-center" style={{ color: "var(--wofy-text-soft)" }}>Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;

  return (
    <div data-testid="admin-dashboard" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="overline mb-3">ADMIN · SOCIAL PULSE</div>
          <h1 className="font-display text-4xl md:text-5xl leading-[1.05]">Studio &amp; moderation.</h1>
        </div>
        <AdminHealthCard />
      </div>

      <div className="mt-8 flex flex-wrap gap-1 border-b" style={{ borderColor: "var(--wofy-border)" }}>
        {TABS.map((t) => {
          const Ic = t.icon;
          const active = tab === t.v;
          return (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`admin-tab-${t.v}`}
              className={`px-4 py-3 -mb-px text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${active ? "" : "border-transparent hover:opacity-80"}`}
              style={active ? { borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" } : { color: "var(--wofy-text-soft)" }}
            >
              <Ic className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-10">
        {tab === "content" && <ContentPanel />}
        {tab === "causes" && <AdminCauses />}
        {tab === "organizations" && <AdminOrganizations />}
        {tab === "world-changers" && <AdminWorldChangers />}
        {tab === "in-memoriam" && <AdminInMemoriam />}
        {tab === "events" && <AdminEvents />}
        {tab === "pulse" && <AdminPulseItems />}
        {tab === "locations" && <AdminLocations />}
        {tab === "services" && <AdminServices />}
        {tab === "memorials" && <AdminMemorials />}
        {tab === "news" && <AdminNewsSources />}
        {tab === "news-illustrations" && <AdminNewsIllustrations />}
        {tab === "news-submissions" && <AdminNewsSubmissions />}
        {tab === "moderation" && <AdminModeration />}
        {tab === "family-comments" && <AdminFamilyComments />}
        {tab === "recipes" && <AdminRecipes />}
        {tab === "nav" && <AdminNavCategories />}
        {tab === "public" && <AdminPublicSubmissions />}
        {tab === "users" && <AdminUsers />}
        {tab === "verifications" && <AdminUserVerifications />}
        {tab === "submissions" && <AdminSubmissions />}
        {tab === "messages" && <AdminMessages />}
        {tab === "backup" && <AdminBackup />}
        {tab === "premium" && <AdminPremium />}
        {tab === "retention" && <AdminRetention />}
        {tab === "access" && <AdminAccess />}
        {tab === "marketing" && <AdminMarketingSignups />}
        {tab === "network" && <AdminNetworkSites />}
        {tab === "satellites" && <AdminSatellites />}
        {tab === "seeder" && <AdminSeederConsole />}
        {tab === "email-tester" && <AdminEmailTester />}
        {tab === "cemeteries" && <AdminCemeteryEditor />}
      </div>
    </div>
  );
}

function ContentPanel() {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [loadingList, setLoadingList] = useState(true);

  const fetchArticles = async () => {
    setLoadingList(true);
    try {
      const { data } = await http.get("/articles", { params: { include_unpublished: true } });
      setArticles(data);
    } catch (e) { toast.error("Failed to load articles"); }
    finally { setLoadingList(false); }
  };
  useEffect(() => { fetchArticles(); }, []);

  if (editing) {
    return (
      <ArticleEditor
        article={editing === "new" ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchArticles(); }}
      />
    );
  }

  const filtered = filter === "all" ? articles : articles.filter((a) => a.section === filter);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline mb-2">CONTENT STUDIO</div>
          <h2 className="font-display text-3xl">Publish new stories &amp; appeals.</h2>
          <p className="mt-2 max-w-2xl text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Create Change Maker profiles, Emergency Appeals, and Success Stories.
          </p>
        </div>
        <button
          data-testid="new-article-btn"
          onClick={() => setEditing("new")}
          className="wofy-btn-primary"
        >
          <Plus className="w-4 h-4" /> New article
        </button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2 border-b pb-4" style={{ borderColor: "var(--wofy-border)" }}>
        {["all", "change_maker", "appeal", "story"].map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${filter === f ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={filter === f ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : {}}
          >
            {f === "all" ? "All" : SECTION_LABELS[f]}
            <span className="ml-2 text-xs font-mono opacity-60">
              {f === "all" ? articles.length : articles.filter((a) => a.section === f).length}
            </span>
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-4">
        {loadingList && <div className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading articles…</div>}
        {!loadingList && filtered.length === 0 && (
          <div className="wofy-card text-center py-16">
            <div className="font-display text-2xl">No articles yet.</div>
            <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              Click <em>New article</em> to publish your first Change Maker, Appeal, or Story.
            </p>
          </div>
        )}
        {filtered.map((a) => (
          <div key={a.slug} data-testid={`admin-article-${a.slug}`} className="wofy-card flex flex-wrap items-center gap-6">
            <div className="w-24 h-24 rounded-2xl overflow-hidden shrink-0" style={{ background: "var(--wofy-surface)" }}>
              {a.featured_image ? (
                <img src={a.featured_image} alt={a.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>NO IMAGE</div>
              )}
            </div>
            <div className="flex-1 min-w-[260px]">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="wofy-tag">{SECTION_LABELS[a.section] || a.section}</span>
                {a.published ? (
                  <span className="inline-flex items-center gap-1 text-xs font-mono uppercase" style={{ color: "var(--wofy-primary)" }}>
                    <Eye className="w-3 h-3" /> Published
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>
                    <EyeOff className="w-3 h-3" /> Draft
                  </span>
                )}
              </div>
              <div className="font-display text-xl leading-tight">{a.title}</div>
              {a.subtitle && (
                <p className="mt-1 text-sm line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{a.subtitle}</p>
              )}
              <div className="mt-2 text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>/{a.slug}</div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={
                  a.section === "change_maker"
                    ? `/change-makers/${a.slug}`
                    : a.section === "appeal"
                    ? `/offer-help/${a.slug}`
                    : `/stories/${a.slug}`
                }
                target="_blank"
                data-testid={`view-${a.slug}`}
                className="wofy-btn-ghost"
                aria-label="View live"
              >
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <button
                data-testid={`edit-${a.slug}`}
                onClick={() => setEditing(a)}
                className="wofy-btn-ghost"
                aria-label="Edit"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                data-testid={`delete-${a.slug}`}
                onClick={async () => {
                  if (!window.confirm(`Delete "${a.title}"?`)) return;
                  try {
                    await http.delete(`/articles/${a.slug}`);
                    toast.success("Article deleted");
                    fetchArticles();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Delete failed");
                  }
                }}
                className="wofy-btn-ghost"
                style={{ color: "#B23A3A" }}
                aria-label="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminHealthCard() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get("/admin/diagnostics");
      setStats(data.collections);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const reseed = async () => {
    if (!window.confirm("Re-run all content seeds (memorials, notable people, events, news sources)? This is idempotent and safe.")) return;
    setBusy(true);
    try {
      const { data } = await http.post("/admin/reseed");
      toast.success("Reseed complete — news ingestion running in background.");
      // Refresh diagnostics after 3s to reflect changes.
      setTimeout(load, 3000);
      console.log("Reseed:", data.results);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reseed failed");
    } finally {
      setBusy(false);
    }
  };

  if (!stats) return null;
  const items = [
    ["News", stats.news_articles],
    ["Memorials", stats.memorials],
    ["Events", stats.events],
    ["World Changers", stats.world_changers],
    ["Users", stats.users],
    ["Organizations", stats.organizations],
  ];
  return (
    <div className="wofy-card min-w-[280px]" data-testid="admin-health-card">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">CONTENT HEALTH</div>
        <button
          onClick={reseed}
          disabled={busy}
          data-testid="admin-reseed"
          className="text-[11px] font-mono uppercase tracking-wider underline disabled:opacity-50"
          style={{ color: "var(--wofy-primary)" }}
        >
          {busy ? "Reseeding…" : "Reseed content"}
        </button>
      </div>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {items.map(([label, n]) => (
          <li key={label} className="flex items-baseline justify-between gap-2">
            <span style={{ color: "var(--wofy-text-soft)" }}>{label}</span>
            <span className={`font-mono ${n === 0 ? "text-red-600" : ""}`}>{n?.toLocaleString?.() ?? "—"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

